import { Capability, getComputedRole, hasSectionCapability } from "../../shared/capabilities";
import { SectionRole } from "../../shared/types/roles";
import { DEFAULT_CODE_KERNEL_LANG } from "../../shared/worksheets/code-kernels";

import {
  courseSelectors,
  createAppSelector,
  createStringArraySelector,
  sectionSelectors,
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  termSelectors,
  userSelectors,
} from "./base";
import { selectPresentingUserId } from "./class-session";
import { selectClassUserIdByUserIdMap, selectHandRaisedAtByUserIdMap } from "./class-user";
import { selectCourseRoleByCourseIdMap } from "./course-user";
import { selectArchivedSectionIds, selectArchivedTermIds, selectCurrentSection } from "./current";
import { selectCurrentOrgRole } from "./org";
import { selectCurrentUserRole } from "./permissions";
import {
  selectActivityStatusBySectionUserIdMap,
  selectActivityStatusByUserIdMap,
  selectIsFollowingByUserIdMap,
} from "./section-activity";
import {
  selectEnabledSectionUserIdsForCurrentSection,
  selectEnabledSectionUsersForCurrentSection,
  selectSectionRoleBySectionIdMap,
} from "./section-user";
import { compareSectionsByActiveTitle, compareUserIdsForPeopleList } from "./sorting";
import { SectionUserActivityStatus } from "./types";
import { selectAdminSectionId, selectIsStudentPreview } from "./view";
import { selectScreensharingClassUserId } from "./virtual-classes";

import { type FullSectionType } from "components/server-types";
import { ORDER_BY_FULL_TITLE, ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { RootState } from "store";
import { compactMap, filterMap } from "utils/collections";
import { getCourseAndSectionDisplayTitle, getCourseCodeAndSectionTitle } from "utils/course";
import { isLinkedSection, isTemplateSection, isUnlinkedSection } from "utils/publishing";
import { HOME_ROLE_TO_SECTION_ROLES, HomeRole } from "utils/urls";

export const selectCurrentSectionKind = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.kind,
);

export const selectCurrentSectionIsLinked = createAppSelector(
  [selectCurrentSection],
  (currentSection) => isLinkedSection(currentSection),
);

export const selectCurrentSectionIsTemplate = createAppSelector(
  [selectCurrentSection],
  (currentSection) => isTemplateSection(currentSection),
);

export const selectCurrentSectionIsUnlinked = createAppSelector(
  [selectCurrentSection],
  (currentSection) => isUnlinkedSection(currentSection),
);

// Selects the admin section if set, otherwise the current section.
export const selectDesignatedSectionId = createAppSelector(
  [selectAdminSectionId, selectCurrentSectionId],
  (adminSectionId, currentSectionId) => adminSectionId ?? currentSectionId,
);

export const selectDesignatedSection = createAppSelector(
  [selectDesignatedSectionId, sectionSelectors.selectEntities],
  (sectionId, sections) => (sectionId && sectionId in sections ? sections[sectionId] : null),
);

export const selectCurrentSectionKernelLang = createAppSelector(
  [selectCurrentSection],
  (section) => section?.code_blocks_default_language || DEFAULT_CODE_KERNEL_LANG,
);

/** Active (non-archived) sections with course display titles, sorted by full_title. */
export const selectFullSectionsSorted = createAppSelector(
  [sectionSelectors.selectAll, courseSelectors.selectEntities, selectArchivedSectionIds],
  (sections, courseById, archivedSectionIds): FullSectionType[] => {
    const archivedSectionIdSet = new Set(archivedSectionIds);
    return compactMap(sections, (section) => {
      const course = courseById[section.course_id];
      if (!course || archivedSectionIdSet.has(section.id)) {
        return null;
      }
      return {
        ...section,
        full_title: getCourseAndSectionDisplayTitle(course, section),
        course_code_section_title: getCourseCodeAndSectionTitle(course, section),
      };
    }).toSorted(ORDER_BY_FULL_TITLE);
  },
);

/**
 * Active (non-archived) section ids for SectionMenu.
 * Order follows `sectionSelectors.selectAll` (title). Optional filters: term, course, templates only.
 */
export const selectSectionMenuIds = createStringArraySelector(
  [
    sectionSelectors.selectAll,
    selectArchivedSectionIds,
    (_state: RootState, termId: string | null) => termId,
    (_state: RootState, _termId: string | null, courseId: string | null) => courseId,
    (_state: RootState, _termId: string | null, _courseId: string | null, templatesOnly: boolean) =>
      templatesOnly,
  ],
  (sections, archivedSectionIds, termId, courseId, templatesOnly): string[] => {
    const archivedSectionIdSet = new Set(archivedSectionIds);
    return filterMap(
      sections,
      (section) =>
        !archivedSectionIdSet.has(section.id) &&
        (termId == null || section.term_id === termId) &&
        // null/undefined = no filter; "" (e.g. AddSectionModal before course pick) must not match.
        (courseId == null || section.course_id === courseId) &&
        (!templatesOnly || isTemplateSection(section)),
      (section) => section.id,
    );
  },
);

/**
 * Listbox options ({ id, title }) for SectionMenu, same filters as selectSectionMenuIds.
 * Sorted by display title (course + section).
 */
export const selectSectionMenuOptions = createAppSelector(
  [selectSectionMenuIds, sectionSelectors.selectEntities, (state: RootState) => state.courses],
  (sectionIds, sectionsById, courses) =>
    compactMap(sectionIds, (sectionId) => {
      const section = sectionsById[sectionId];
      if (!section) {
        return null;
      }
      const course = courses[section.course_id];
      if (!course) {
        return null;
      }
      return {
        id: sectionId,
        title: getCourseAndSectionDisplayTitle(course, section),
      };
    }).toSorted(ORDER_BY_TITLE),
);

/** Section ids for the current user under the given home role. */
export const selectSectionIdsForCurrentUser = createStringArraySelector(
  [
    (state, role: HomeRole | null | undefined) => role,
    sectionSelectors.selectIds,
    sectionUserSelectors.filterForCurrentUser,
  ],
  (role, sectionIds, sectionUsers): string[] => {
    if (role === HomeRole.ADMIN) {
      return sectionIds;
    }
    if (!role) {
      return [];
    }
    const sectionRole = HOME_ROLE_TO_SECTION_ROLES[role];
    const sectionIdsForRole = new Set(
      filterMap(
        sectionUsers,
        (sectionUser) => sectionUser.role === sectionRole,
        (sectionUser) => sectionUser.section_id,
      ),
    );
    return sectionIds.filter((sectionId) => sectionIdsForRole.has(sectionId));
  },
);

/**
 * Linked section ids for the current template section.
 * Order follows `sectionSelectors.selectAll` (title).
 */
export const selectLinkedSectionIdsForCurrentSection = createStringArraySelector(
  [selectCurrentSection, sectionSelectors.selectAll],
  (currentSection, sections): string[] => {
    if (!currentSection || !isTemplateSection(currentSection)) {
      return [];
    }
    return filterMap(
      sections,
      (section) => isLinkedSection(section) && section.template_id === currentSection.id,
      (section) => section.id,
    );
  },
);

export const selectCurrentCourseCode = createAppSelector(
  [selectCurrentSection, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.code) || null,
);

export const selectCurrentCourseTitle = createAppSelector(
  [selectCurrentSection, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.title) || null,
);

export const selectCurrentCourseId = createAppSelector(
  [selectCurrentSection],
  (section) => section?.course_id || null,
);

export const selectCourseCodeBySectionId = createAppSelector(
  [sectionSelectors.selectById, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.code) || null,
);

export const selectLmsSectionId = createAppSelector(
  [selectDesignatedSection],
  (designatedSection) => designatedSection?.lms_section_id || null,
);

export const selectCurrentSectionTitle = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.title || null,
);

export const selectSectionTitleBySectionId = createAppSelector(
  [sectionSelectors.selectById],
  (section) => section?.title || null,
);

export const selectCurrentSectionDescription = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.description || null,
);

export const selectCurrentSectionAvEnabled = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.av_enabled || false,
);

export const selectUserIdsInCurrentSection = createStringArraySelector(
  [selectEnabledSectionUsersForCurrentSection],
  (sectionUsers) => sectionUsers.map((cu) => cu.user_id),
);

/**
 * All section user ids in people-list order. Downstream filters (role, disabled, in-class)
 * preserve relative order.
 */
export const selectSortedUserIdsInCurrentSection = createStringArraySelector(
  [
    sectionUserSelectors.filterForCurrentSection,
    selectClassUserIdByUserIdMap,
    selectHandRaisedAtByUserIdMap,
    selectIsFollowingByUserIdMap,
    selectActivityStatusByUserIdMap,
    userSelectors.selectEntities,
    selectPresentingUserId,
    selectScreensharingClassUserId,
  ],
  (
    sectionUsers,
    classUserIdByUserId,
    handRaisedAtByUserId,
    isFollowingByUserId,
    activityStatusByUserId,
    usersById,
    presentingUserId,
    screensharingClassUserId,
  ) => {
    const lookups = {
      classUserIdByUserId,
      handRaisedAtByUserId,
      isFollowingByUserId,
      activityStatusByUserId,
      usersById,
      presentingUserId,
      screensharingClassUserId,
    };

    return sectionUsers
      .map((sectionUser) => sectionUser.user_id)
      .toSorted((userIdA, userIdB) => compareUserIdsForPeopleList(userIdA, userIdB, lookups));
  },
);

/** Enabled user ids with the given role, in people-list order. */
export const selectEnabledUserIdsInRoleForCurrentSection = createStringArraySelector(
  [
    selectSortedUserIdsInCurrentSection,
    selectEnabledSectionUsersForCurrentSection,
    (_state: unknown, role: SectionRole) => role,
  ],
  (sortedUserIds, enabledSectionUsers, role) => {
    const userIdsInRole = new Set(
      filterMap(
        enabledSectionUsers,
        (sectionUser) => sectionUser.role === role,
        (sectionUser) => sectionUser.user_id,
      ),
    );
    return sortedUserIds.filter((userId) => userIdsInRole.has(userId));
  },
);

/** Disabled user ids in the current section, in people-list order. */
export const selectDisabledUserIdsInCurrentSection = createStringArraySelector(
  [selectSortedUserIdsInCurrentSection, selectEnabledSectionUsersForCurrentSection],
  (sortedUserIds, enabledSectionUsers) => {
    const enabledUserIds = new Set(enabledSectionUsers.map((sectionUser) => sectionUser.user_id));
    return sortedUserIds.filter((userId) => !enabledUserIds.has(userId));
  },
);

/** Comma-joined emails of enabled section users except the current user (for mailto). */
export const selectEnabledUserEmailsForCurrentSection = createAppSelector(
  [selectUserIdsInCurrentSection, userSelectors.selectEntities, selectCurrentUserId],
  (userIds, usersById, currentUserId) =>
    compactMap(userIds, (userId) => {
      if (userId === currentUserId) {
        return null;
      }
      return usersById[userId]?.email || null;
    }).join(","),
);

export const selectOnlineUserCountInCurrentSection = createAppSelector(
  [selectEnabledSectionUserIdsForCurrentSection, selectActivityStatusBySectionUserIdMap],
  (sectionUserIds, statusBySectionUserId) =>
    sectionUserIds.filter(
      (sectionUserId) => statusBySectionUserId[sectionUserId] !== SectionUserActivityStatus.OFFLINE,
    ).length,
);

/**
 * Term ids that have ≥1 section in the course.
 * When `archived` is false: non-archived terms.
 * When `archived` is true: archived terms.
 * Order follows `termSelectors.selectIds`.
 */
export const selectTermIdsWithSectionsByCourse = createStringArraySelector(
  [
    (_state: RootState, courseId: string) => courseId,
    (_state: RootState, _courseId: string, archived: boolean) => archived,
    termSelectors.selectIds,
    selectArchivedTermIds,
    sectionSelectors.selectAll,
  ],
  (courseId, archived, termIds, archivedTermIds, sections): string[] => {
    const termIdsWithSections = new Set(
      compactMap(sections, (section) => (section.course_id === courseId ? section.term_id : null)),
    );
    const archivedTermIdSet = new Set(archivedTermIds);
    return termIds.filter(
      (termId) =>
        termIdsWithSections.has(termId) &&
        (archived ? archivedTermIdSet.has(termId) : !archivedTermIdSet.has(termId)),
    );
  },
);

export const selectCurrentTerm = createAppSelector(
  [selectCurrentSection, (state) => state.terms],
  (currentSection, terms) => (currentSection?.term_id && terms[currentSection?.term_id]) || null,
);

export const selectCurrentTermId = createAppSelector(
  [selectCurrentSection],
  (section) => section?.term_id || null,
);

export const selectCurrentTermTitle = createAppSelector(
  [selectCurrentTerm],
  (currentTerm) => currentTerm?.title || null,
);

/**
 * Section ids for a term/course filter.
 * Order follows `sectionSelectors.selectAll` (title).
 */
export const selectSectionIdsByTermAndCourse = createStringArraySelector(
  [
    (state, termId: string | null) => termId,
    (state, termId: string | null, courseId: string | null) => courseId,
    sectionSelectors.selectAll,
  ],
  (termId, courseId, sections): string[] =>
    filterMap(
      sections,
      (section) =>
        (!termId || section.term_id === termId) && (!courseId || section.course_id === courseId),
      (section) => section.id,
    ),
);

/**
 * Term ids for the home section list for the current user under the given home role.
 * When `archived` is false: non-archived terms with at least one active section.
 * When `archived` is true: terms that have sections but are not in the active set.
 */
export const selectHomeTermIdsForCurrentUser = createStringArraySelector(
  [
    selectSectionIdsForCurrentUser,
    sectionSelectors.selectEntities,
    (_state: RootState, _role: HomeRole | null | undefined, archived: boolean) => archived,
    termSelectors.selectIds,
    selectArchivedTermIds,
    selectArchivedSectionIds,
  ],
  (sectionIds, sectionsById, archived, termIds, archivedTermIds, archivedSectionIds): string[] => {
    const archivedSectionIdSet = new Set(archivedSectionIds);
    const archivedTermIdSet = new Set(archivedTermIds);
    const termIdsWithSections = new Set<string>();
    const termIdsWithActiveSection = new Set<string>();
    for (const sectionId of sectionIds) {
      const section = sectionsById[sectionId];
      if (section?.term_id) {
        termIdsWithSections.add(section.term_id);
        if (!archivedSectionIdSet.has(section.id)) {
          termIdsWithActiveSection.add(section.term_id);
        }
      }
    }
    return termIds.filter((termId) => {
      if (!termIdsWithSections.has(termId)) {
        return false;
      }
      const isActiveTerm = !archivedTermIdSet.has(termId) && termIdsWithActiveSection.has(termId);
      return archived ? !isActiveTerm : isActiveTerm;
    });
  },
);

/**
 * Section ids for the current user in a term.
 * Order follows `sectionSelectors.selectAll` (title), preserved through the role filter.
 */
export const selectSectionIdsForCurrentUserAndTerm = createStringArraySelector(
  [
    selectSectionIdsForCurrentUser,
    sectionSelectors.selectEntities,
    (_state: RootState, _role: HomeRole | null | undefined, termId: string) => termId,
  ],
  (sectionIds, sectionsById, termId): string[] =>
    sectionIds.filter((sectionId) => sectionsById[sectionId]?.term_id === termId),
);

/** Section ids for a term for the current user, archived-last then title order. */
export const selectHomeSectionIdsForTerm = createStringArraySelector(
  [
    selectSectionIdsForCurrentUserAndTerm,
    sectionSelectors.selectEntities,
    selectArchivedSectionIds,
  ],
  (sectionIds, sectionsById, archivedSectionIds): string[] => {
    const archivedSectionIdSet = new Set(archivedSectionIds);
    return compactMap(sectionIds, (sectionId) => sectionsById[sectionId] ?? null)
      .toSorted((left, right) => compareSectionsByActiveTitle(left, right, archivedSectionIdSet))
      .map((section) => section.id);
  },
);

/**
 * Root section ids for the admin section list, archived-last then title order.
 * When CCM is on, only templates and unlinked sections (linked nest under templates).
 */
export const selectAdminSectionIdsByTermAndCourse = createStringArraySelector(
  [
    selectSectionIdsByTermAndCourse,
    sectionSelectors.selectEntities,
    (_state: RootState, _termId: string | null, _courseId: string | null, ccmEnabled: boolean) =>
      ccmEnabled,
    selectArchivedSectionIds,
  ],
  (sectionIds, sectionsById, ccmEnabled, archivedSectionIds): string[] => {
    const archivedSectionIdSet = new Set(archivedSectionIds);
    const sections = compactMap(sectionIds, (sectionId) => sectionsById[sectionId] ?? null);
    return (
      ccmEnabled
        ? sections.filter((section) => isTemplateSection(section) || isUnlinkedSection(section))
        : sections
    )
      .toSorted((left, right) => compareSectionsByActiveTitle(left, right, archivedSectionIdSet))
      .map((section) => section.id);
  },
);

/**
 * Linked section ids under a template within a term/course filter, archived-last then title
 * order. Empty when CCM is off (linked sections are shown as roots instead).
 */
export const selectAdminLinkedSectionIdsByTemplateId = createStringArraySelector(
  [
    selectSectionIdsByTermAndCourse,
    sectionSelectors.selectEntities,
    (_state: RootState, _termId: string | null, _courseId: string | null, templateId: string) =>
      templateId,
    (
      _state: RootState,
      _termId: string | null,
      _courseId: string | null,
      _templateId: string,
      ccmEnabled: boolean,
    ) => ccmEnabled,
    selectArchivedSectionIds,
  ],
  (sectionIds, sectionsById, templateId, ccmEnabled, archivedSectionIds): string[] => {
    if (!ccmEnabled) {
      return [];
    }
    const archivedSectionIdSet = new Set(archivedSectionIds);
    return compactMap(sectionIds, (sectionId) => sectionsById[sectionId] ?? null)
      .filter((section) => isLinkedSection(section) && section.template_id === templateId)
      .toSorted((left, right) => compareSectionsByActiveTitle(left, right, archivedSectionIdSet))
      .map((section) => section.id);
  },
);

/** Section ids for a term/course filter excluding the current section, in title order. */
export const selectSectionIdsByTermAndCourseExceptCurrent = createStringArraySelector(
  [selectSectionIdsByTermAndCourse, selectCurrentSectionId],
  (sectionIds, currentSectionId): string[] =>
    currentSectionId
      ? sectionIds.filter((sectionId) => sectionId !== currentSectionId)
      : sectionIds,
);

/** Template section ids. */
export const selectTemplateSectionIds = createStringArraySelector(
  [sectionSelectors.selectAll],
  (sections): string[] =>
    compactMap(sections, (section) => (isTemplateSection(section) ? section.id : null)),
);

/** Non-template section ids for a term/course filter, in title order. */
export const selectNonTemplateSectionIdsByTermAndCourse = createStringArraySelector(
  [selectSectionIdsByTermAndCourse, selectTemplateSectionIds],
  (sectionIds, templateSectionIds): string[] => {
    const templateIdSet = new Set(templateSectionIds);
    return sectionIds.filter((sectionId) => !templateIdSet.has(sectionId));
  },
);

/**
 * Section ids the current user can author (full-title order), resolved per-section via
 * getComputedRole + CAN_AUTHOR_SECTION (scoped-first precedence including User.role).
 */
export const selectAuthorableSectionIds = createStringArraySelector(
  [
    selectIsStudentPreview,
    sectionSelectors.selectAll,
    (state) => state.courses,
    selectSectionRoleBySectionIdMap,
    selectCourseRoleByCourseIdMap,
    selectCurrentOrgRole,
    selectCurrentUserRole,
  ],
  (
    isStudentPreview,
    sections,
    courses,
    sectionRoleBySectionId,
    courseRoleByCourseId,
    orgRole,
    userRole,
  ): string[] => {
    if (isStudentPreview) {
      return [];
    }

    const authorableSections = sections.filter((section) => {
      const course = courses[section.course_id];
      if (!course) {
        return false;
      }
      const computedRole = getComputedRole({
        userRole,
        sectionRole: sectionRoleBySectionId[section.id] ?? null,
        orgRole,
        courseRole: courseRoleByCourseId[section.course_id] ?? null,
      });
      return hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    });

    return authorableSections
      .map((section) => ({
        id: section.id,
        full_title: getCourseAndSectionDisplayTitle(courses[section.course_id], section),
      }))
      .toSorted(ORDER_BY_FULL_TITLE)
      .map((section) => section.id);
  },
);
